import { Component, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { EventBusStatus } from "./components/EventBusStatus.jsx";
import { installMinioAssetRewriter } from "./modules/minio-assets.js";
import { SiteHeader, SearchPanel } from "./homepage/components/SiteHeader.jsx";
import {
  ContactCta,
  HeroCarousel,
  IndustriesSection,
  PartnersAndIntro,
  SolutionsShowcase,
  VirtualTourSection,
} from "./homepage/components/HomeSections.jsx";
import {
  AssessmentSection,
  SiteFooter,
  TourModal,
} from "./homepage/components/InteractivePanels.jsx";

class HomepageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("Homepage React error", error, info);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="sq-react-error" role="alert">
        <strong>Halaman tidak dapat ditampilkan.</strong>
        <button type="button" onClick={() => location.reload()}>
          Muat ulang
        </button>
      </div>
    );
  }
}

function HomepageApp({ config }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [navigating, setNavigating] = useState(false);
  useEffect(() => {
    const uninstall = installMinioAssetRewriter();
    const onClick = (event) => {
      const link = event.target.closest?.("a[href]");
      if (
        !link ||
        event.defaultPrevented ||
        link.target === "_blank" ||
        link.hasAttribute("download")
      )
        return;
      const target = new URL(link.href, location.href);
      if (
        target.origin === location.origin &&
        target.href !== location.href &&
        !target.hash
      )
        setNavigating(true);
    };
    const stop = () => setNavigating(false);
    document.addEventListener("click", onClick);
    window.addEventListener("pageshow", stop);
    return () => {
      uninstall();
      document.removeEventListener("click", onClick);
      window.removeEventListener("pageshow", stop);
    };
  }, []);
  return (
    <>
      <div
        className={`sq-react-route-progress${navigating ? " is-active" : ""}`}
        aria-hidden="true"
      />
      <SiteHeader config={config} onSearch={() => setSearchOpen(true)} />
      <main>
        <HeroCarousel config={config} />
        <PartnersAndIntro config={config} />
        <SolutionsShowcase config={config} />
        <IndustriesSection config={config} />
        <AssessmentSection config={config} />
        <VirtualTourSection config={config} onStart={() => setTourOpen(true)} />
        <ContactCta config={config} />
      </main>
      <SiteFooter config={config} />
      <EventBusStatus variant="frontend" />
      <SearchPanel
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        config={config}
      />
      <TourModal
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        config={config}
      />
    </>
  );
}

function updateDocumentMetadata(config) {
  const homepage = config?.homepage || {};
  if (homepage.title) document.title = homepage.title;
  const values = {
    description: homepage.meta_desc,
    keywords: homepage.meta_keywords,
  };
  Object.entries(values).forEach(([name, content]) => {
    if (!content) return;
    let element = document.head.querySelector(`meta[name="${name}"]`);
    if (!element) {
      element = document.createElement("meta");
      element.name = name;
      document.head.appendChild(element);
    }
    element.content = content;
  });
}

function HomepageBootstrap({ apiUrl }) {
  const [state, setState] = useState({ config: null, error: "" });

  useEffect(() => {
    const controller = new AbortController();
    fetch(apiUrl, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload)
          throw new Error(payload?.message || "Data homepage gagal dimuat.");
        updateDocumentMetadata(payload);
        setState({ config: payload, error: "" });
      })
      .catch((error) => {
        if (error.name !== "AbortError")
          setState({ config: null, error: error.message });
      });
    return () => controller.abort();
  }, [apiUrl]);

  if (state.error) {
    return (
      <main className="sq-bootstrap-state" role="alert">
        <img src="/media/static/templates/fe/images/squra.png" alt="SQURA" />
        <h1>Homepage belum dapat dimuat</h1>
        <p>{state.error}</p>
        <button type="button" onClick={() => location.reload()}>
          Coba lagi
        </button>
      </main>
    );
  }

  if (!state.config) {
    return (
      <div
        className="sq-bootstrap-state is-loading"
        role="status"
        aria-live="polite"
      >
        <img src="/media/static/templates/fe/images/squra.png" alt="SQURA" />
        <span className="sq-bootstrap-spinner" aria-hidden="true" />
        <p>Menyiapkan pengalaman SQURA…</p>
      </div>
    );
  }

  return <HomepageApp config={state.config} />;
}

const root = document.getElementById("homepage-root");
if (root) {
  createRoot(root).render(
    <HomepageErrorBoundary>
      <HomepageBootstrap apiUrl={root.dataset.apiUrl} />
    </HomepageErrorBoundary>,
  );
}
